import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Trophy,
  Medal,
  Award,
  User,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ExecutiveData, DashboardPeriod } from '@/types/salesDashboard';

// Format currency in Indian format
const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Get initials from name
const getInitials = (name: string) => {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

// Get rank badge
const getRankBadge = (rank: number) => {
  switch (rank) {
    case 1:
      return <Trophy className="w-5 h-5 text-yellow-500" />;
    case 2:
      return <Medal className="w-5 h-5 text-gray-400" />;
    case 3:
      return <Award className="w-5 h-5 text-amber-600" />;
    default:
      return (
        <span className="w-5 h-5 flex items-center justify-center text-sm font-medium text-gray-500">
          {rank}
        </span>
      );
  }
};

// Get progress color based on percentage
const getProgressColor = (progress: number) => {
  if (progress >= 100) return 'bg-green-500';
  if (progress >= 80) return 'bg-blue-500';
  if (progress >= 50) return 'bg-yellow-500';
  return 'bg-red-500';
};

type SortField =
  | 'name'
  | 'activeLeads'
  | 'followUpsDone'
  | 'proposalsSent'
  | 'dealsWon'
  | 'revenueAchieved'
  | 'targetProgress'
  | 'paymentsReceived';

type SortOrder = 'asc' | 'desc';

interface ExecutiveTableProps {
  executives: ExecutiveData[];
  period: DashboardPeriod;
  isLoading?: boolean;
  onExecutiveClick?: (executiveId: string) => void;
}

export const ExecutiveTable: React.FC<ExecutiveTableProps> = ({
  executives,
  period,
  isLoading,
  onExecutiveClick,
}) => {
  const [sortField, setSortField] = useState<SortField>('revenueAchieved');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const sortedExecutives = [...executives].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];

    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = (bValue as string).toLowerCase();
      return sortOrder === 'asc'
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    }

    // Null-safe numeric sort: treat null (e.g. targetProgress with no target)
    // as -Infinity on ascending sort so rows with "no target" go to the bottom
    // in the default desc view and to the top in asc view.
    const aNum = aValue === null || aValue === undefined ? -Infinity : (aValue as number);
    const bNum = bValue === null || bValue === undefined ? -Infinity : (bValue as number);

    return sortOrder === 'asc' ? aNum - bNum : bNum - aNum;
  });

  const SortHeader: React.FC<{
    field: SortField;
    children: React.ReactNode;
    className?: string;
  }> = ({ field, children, className = '' }) => (
    <th
      className={`px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors ${className}`}
      onClick={() => handleSort(field)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSort(field); } }}
      tabIndex={0}
      role="columnheader"
      aria-sort={sortField === field ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortField === field ? (
          sortOrder === 'asc' ? (
            <ArrowUp className="w-3 h-3" />
          ) : (
            <ArrowDown className="w-3 h-3" />
          )
        ) : (
          <ArrowUpDown className="w-3 h-3 opacity-30" />
        )}
      </div>
    </th>
  );

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Executive Performance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 bg-gray-100 animate-pulse rounded-lg" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            Executive Performance
          </CardTitle>
          <Badge variant="outline" className="text-xs">
            {executives.length} executives
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-y border-gray-200">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8">
                  #
                </th>
                <SortHeader field="name" className="min-w-[160px]">
                  Executive
                </SortHeader>
                <SortHeader field="activeLeads">Active</SortHeader>
                <SortHeader field="followUpsDone">Follow-ups</SortHeader>
                <SortHeader field="proposalsSent">Proposals</SortHeader>
                <SortHeader field="dealsWon">Won</SortHeader>
                <SortHeader field="revenueAchieved" className="min-w-[120px]">
                  Revenue
                </SortHeader>
                <SortHeader field="targetProgress" className="min-w-[100px]">
                  Target %
                </SortHeader>
                <SortHeader field="paymentsReceived" className="min-w-[120px]">
                  Payments
                </SortHeader>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sortedExecutives.map((exec, index) => (
                <motion.tr
                  key={exec.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                  onClick={() => onExecutiveClick?.(exec.id)}
                  onKeyDown={onExecutiveClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onExecutiveClick(exec.id); } } : undefined}
                  tabIndex={onExecutiveClick ? 0 : undefined}
                  role={onExecutiveClick ? 'row' : undefined}
                  aria-label={onExecutiveClick ? `${exec.name} - ${formatCurrency(exec.revenueAchieved)} revenue, ${exec.targetProgress === null ? 'no target set' : `${exec.targetProgress}% target`}. Click to view leads.` : undefined}
                  className="hover:bg-gray-50 cursor-pointer transition-colors focus:outline-none focus:bg-blue-50"
                >
                  <td className="px-3 py-3 whitespace-nowrap">
                    <div className="flex items-center justify-center">
                      {getRankBadge(exec.rank)}
                    </div>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        {exec.avatar ? (
                          <AvatarImage src={exec.avatar} alt={exec.name} />
                        ) : null}
                        <AvatarFallback className="bg-blue-100 text-blue-700 text-xs">
                          {getInitials(exec.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {exec.name}
                        </p>
                        <p className="text-xs text-gray-500">{exec.role}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span className="text-sm font-medium text-gray-900">
                      {exec.activeLeads}
                    </span>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span className="text-sm text-gray-700">
                      {exec.followUpsDone}
                    </span>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span className="text-sm text-gray-700">
                      {exec.proposalsSent}
                    </span>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <Badge
                      variant="outline"
                      className="bg-green-50 text-green-700 border-green-200"
                    >
                      {exec.dealsWon}
                    </Badge>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span className="text-sm font-semibold text-gray-900">
                      {formatCurrency(exec.revenueAchieved)}
                    </span>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    {exec.targetProgress === null ? (
                      <span
                        className="text-sm text-gray-400 italic"
                        title="No target set for the current month"
                      >
                        — No target
                      </span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${getProgressColor(exec.targetProgress)} rounded-full transition-all duration-500`}
                            style={{
                              width: `${Math.min(exec.targetProgress, 100)}%`,
                            }}
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-700 w-10">
                          {exec.targetProgress}%
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span className="text-sm text-gray-700">
                      {formatCurrency(exec.paymentsReceived)}
                    </span>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {executives.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <User className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>No executive data available</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
